import React, { useState, useEffect, useMemo, useCallback } from 'react';
import UIModal from '@/components/shared/UIModal';
import UIButton from '@/components/shared/UIButton';
import UIInputWithLabel from '@/components/shared/UIInputWithLabel';
import { useTickets } from '@/hooks/useTickets';
import useGetDictionaries from '@/hooks/useGetDictionaries';
import { Lease, TicketDictionariesResponse } from '@/types/tickets';
import './CreateTicketModal.scss';
import UISelectWithLabel from '@/components/shared/UISelectWithLabel';
import UITextareaWithLabel from '@/components/shared/UITextareaWithLabel';

interface CreateTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialQuery: string;
  initialCategory?: string;
  onTicketCreated: () => void;
  dictionarySource: 'home' | 'tickets';
}

const formatCategoryLabel = (categoryId: string) => {
  if (!categoryId) return '';
  return categoryId.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

const CreateTicketModal: React.FC<CreateTicketModalProps> = ({
  isOpen,
  onClose,
  initialQuery,
  initialCategory,
  onTicketCreated,
  dictionarySource,
}) => {
  const [title, setTitle] = useState(initialQuery);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState(initialCategory);
  const [selectedLeaseId, setSelectedLeaseId] = useState<number | string>('');

  const { createTicket, loading, error } = useTickets();
  const { data: dictionaries, isLoading: isDictionariesLoading } =
    useGetDictionaries<TicketDictionariesResponse>(dictionarySource);

  const activeLeases = useMemo(
    () =>
      (dictionaries?.leases || []).filter((lease) => lease.status === 'active'),
    [dictionaries?.leases],
  );

  const handleClear = useCallback(() => {
    setTitle(initialQuery);
    setCategory(initialCategory);
    setDescription('');
    if (activeLeases.length === 1) {
      setSelectedLeaseId(activeLeases[0].id);
    } else {
      setSelectedLeaseId('');
    }
  }, [initialQuery, initialCategory, activeLeases]);

  useEffect(() => {
    if (isOpen) {
      handleClear();
    }
  }, [isOpen, handleClear]);

  const handleSubmit = async () => {
    const ticketData: any = {
      title,
      description,
      category,
      leaseId: Number(selectedLeaseId),
    };

    const newTicket = await createTicket(ticketData);
    if (newTicket) {
      onTicketCreated();
      onClose();
    }
  };

  const categoryOptions = dictionaries?.ticketCategories || [];
  const isFormValid = title.trim() && category && selectedLeaseId && !loading;

  return (
    <UIModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create a New Ticket"
      subtitle="* Required fields"
      size="xlarge"
    >
      <div className="CreateTicketModal">
        <div className="CreateTicketModal__form">
          <UIInputWithLabel
            label="Subject *"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Describe your issue briefly..."
            maxLength={200}
            autoFocus={isOpen}
          />

          <UISelectWithLabel
            label="Property / Unit *"
            value={selectedLeaseId}
            onChange={(e) => setSelectedLeaseId(e.target.value)}
            disabled={activeLeases.length === 0}
            placeholder={
              activeLeases.length === 0
                ? 'No active leases found'
                : 'Select a property / unit'
            }
            options={activeLeases.map((lease: Lease) => ({
              value: lease.id,
              label: `${lease.unit?.property?.name} - Unit ${lease.unit?.number}`,
            }))}
          />

          <UISelectWithLabel
            label="Category *"
            value={category || ''}
            onChange={(e) => setCategory(e.target.value)}
            disabled={isDictionariesLoading}
            placeholder={
              isDictionariesLoading
                ? 'Loading categories...'
                : 'Select a category'
            }
            options={categoryOptions.map((cat) => ({
              value: cat,
              label: formatCategoryLabel(cat),
            }))}
          />

          <UITextareaWithLabel
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Please provide detailed information about your issue. Include any relevant details that might help us resolve it faster..."
            rows={3}
            maxLength={1000}
          />
        </div>

        {error && <div className="CreateTicketModal__error">{error}</div>}

        <div className="CreateTicketModal__actions">
          <UIButton
            variant="primary"
            size="medium"
            onClick={handleSubmit}
            disabled={!isFormValid}
          >
            {loading ? 'Submitting...' : 'Create'}
          </UIButton>
          <UIButton
            variant="secondary"
            size="medium"
            outline={true}
            onClick={handleClear}
            disabled={loading}
          >
            Clear
          </UIButton>
        </div>
      </div>
    </UIModal>
  );
};

export default CreateTicketModal;
